/**
 * Interpretation of the official Aqueduct 4.0 annual GDB.
 * This pure interpreter does not select a feature or activate a dataset.
 * Spatial/source acceptance must precede use of an interpreted observation.
 *
 * Primary contracts:
 * https://github.com/wri/Aqueduct40/blob/master/data_dictionary_water-risk-atlas.md
 * https://github.com/wri/Aqueduct40/blob/master/data_FAQ.md
 */
import type { Aqueduct4AnnualObservation } from '../types/aqueduct4';
export type { Aqueduct4AnnualObservation } from '../types/aqueduct4';

const number = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;
const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value : null;
const score = (value: unknown): number | null => {
  const result = number(value);
  return result !== null && result >= 0 && result <= 5 ? result : null;
};
const category = (value: unknown): number | null => {
  const result = number(value);
  return result !== null && Number.isInteger(result) && result >= -1 && result <= 4 ? result : null;
};
const BASELINE_LABELS: Record<number, string> = {
  [-1]: 'Arid and Low Water Use', 0: 'Low (<10%)', 1: 'Low - Medium (10-20%)',
  2: 'Medium - High (20-40%)', 3: 'High (40-80%)', 4: 'Extremely High (>80%)',
};
const OVERALL_LABELS: Record<number, string> = {
  0: 'Low (0-1)', 1: 'Low - Medium (1-2)', 2: 'Medium - High (2-3)',
  3: 'High (3-4)', 4: 'Extremely High (4-5)',
};

/** Preserve model special values without producing a Low score from absence. */
export function interpretAqueduct4Annual(attributes: Record<string, unknown>): Aqueduct4AnnualObservation {
  const stringId = text(attributes.string_id);
  const numericId = number(attributes.aq30_id);
  if (!stringId || numericId === null || !Number.isSafeInteger(numericId) || numericId < 0) {
    throw new Error('Aqueduct 4.0 requires the original annual feature identity');
  }
  const raw = number(attributes.bws_raw);
  const bwsScore = score(attributes.bws_score);
  const bwsCategory = category(attributes.bws_cat);
  const bwsLabel = text(attributes.bws_label);
  const arid = bwsCategory === -1 && raw === 1 && bwsScore === 5;
  const severeScarcity = raw === 9999 && bwsCategory === 4 && bwsScore === 5;
  const ordinary = bwsCategory !== null && bwsCategory >= 0 && raw !== null && raw >= 0 && raw !== 9999;
  const assessed = bwsScore !== null && bwsCategory !== null && bwsLabel === BASELINE_LABELS[bwsCategory]
    && (arid || severeScarcity || ordinary);
  const overallScore = score(attributes.w_awr_def_tot_score);
  const overallCategory = category(attributes.w_awr_def_tot_cat);
  const overallLabel = text(attributes.w_awr_def_tot_label);
  const sourceFraction = number(attributes.w_awr_def_tot_weight_fraction);
  const fraction = sourceFraction !== null && sourceFraction >= 0 && sourceFraction <= 1 ? sourceFraction : null;
  const overallAssessed = overallScore !== null && overallCategory !== null && overallCategory >= 0
    && overallLabel === OVERALL_LABELS[overallCategory] && fraction !== null && fraction > 0;
  return {
    edition: '4.0', scope: 'modeled-basin-province-aquifer', baselineStressPeriod: '1979-2019',
    stringId, numericId, country: text(attributes.name_0), province: text(attributes.name_1),
    baselineStress: {
      status: assessed ? arid ? 'arid-low-water-use' : 'assessed' : 'unavailable',
      score: assessed ? bwsScore : null,
      category: assessed ? bwsCategory : null,
      label: assessed ? bwsLabel! : 'Not established',
      sourceRaw: raw,
      withdrawalToSupplyRatio: assessed && ordinary && !arid ? raw : null,
      rawInterpretation: !assessed ? 'unavailable' : arid ? 'arid-low-water-use'
        : severeScarcity ? 'severe-scarcity-sentinel' : 'ratio',
      concern: !assessed ? 'Unknown' : arid || bwsCategory! >= 3 ? 'High'
        : bwsCategory === 2 ? 'Moderate' : 'Low',
    },
    overallWaterRisk: {
      // Only absorb floating-point summation noise when describing completeness;
      // retain the exact source fraction and score without rounding or rescaling.
      status: !overallAssessed ? 'unavailable' : fraction! < 1 - 1e-12 ? 'assessed-partial-indicators' : 'assessed',
      score: overallAssessed ? overallScore : null,
      category: overallAssessed ? overallCategory : null,
      label: overallAssessed ? overallLabel! : 'Not established',
      availableWeightFraction: fraction,
    },
  };
}
